import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { CollectorTaskPage, TaskStatus } from '../domain/collector-task.model';
import {
  AutoAssignResponse,
  DcEventHistoryResponse,
  DcScheduleAutoAssignFilters,
  DcScheduleDetails,
  DcScheduleFilters,
  DcScheduleResponse,
  ScheduleMarkAssignedResponse,
  TeamLeaderAssignPreview,
  TeamLeaderAssignResult,
  TeamLeaderCoverageConfig,
  TeamLeaderCoverageRow,
  TeamLeadersResponse
} from '../domain/dc-schedule.model';
import {
  DcRosterConfigCreate,
  DcRosterConfigDocument,
  DcRosterConfigEvent,
  DcRosterConfigEventInput,
  DcRosterConfigEventUpdate
} from '../domain/dc-roster-config.model';
import {
  DcRoster,
  DcRosterSaveResponse,
  DcTypeConfig
} from '../domain/dc.roster.model';

export const TASKS_SOURCE_TIME_ZONE = 'Europe/Zurich';

@Injectable({
  providedIn: 'root'
})
export class DcRosterService {
  private readonly baseUrl = environment.API_HOST + '/api/dc-rosters';

  constructor(private http: HttpClient) {}

  getByGameId(gameId: string): Observable<DcRoster> {
    return this.http.get<DcRoster>(`${this.baseUrl}/${gameId}`);
  }

  getConfig(): Observable<DcTypeConfig[]> {
    return this.http.get<DcTypeConfig[]>(`${this.baseUrl}/config`);
  }

  getAdminConfigs(): Observable<DcRosterConfigDocument[]> {
    return this.http
      .get<{ configs: DcRosterConfigDocument[] }>(
        `${this.baseUrl}/config/admin`
      )
      .pipe(map((response) => response.configs));
  }

  countRostersUsingConfig(configId: string): Observable<number> {
    return this.http
      .get<{ rosterCount: number }>(
        `${this.baseUrl}/config/admin/${configId}/usage`
      )
      .pipe(map((response) => response.rosterCount));
  }

  createAdminConfig(
    config: DcRosterConfigCreate
  ): Observable<DcRosterConfigDocument> {
    return this.http.post<DcRosterConfigDocument>(
      `${this.baseUrl}/config/admin`,
      config
    );
  }

  updateAdminConfig(
    configId: string,
    config: Pick<DcRosterConfigDocument, 'dcType' | 'subType' | 'description'>
  ): Observable<DcRosterConfigDocument> {
    return this.http.put<DcRosterConfigDocument>(
      `${this.baseUrl}/config/admin/${configId}`,
      config
    );
  }

  deleteAdminConfig(configId: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(
      `${this.baseUrl}/config/admin/${configId}`
    );
  }

  addAdminCollectionEvent(
    configId: string,
    event: DcRosterConfigEventInput
  ): Observable<DcRosterConfigDocument> {
    return this.http.post<DcRosterConfigDocument>(
      `${this.baseUrl}/config/admin/${configId}/events`,
      event
    );
  }

  updateAdminCollectionEvent(
    configId: string,
    originalCode: string,
    eventUpdate: DcRosterConfigEventUpdate
  ): Observable<DcRosterConfigDocument> {
    return this.http.put<DcRosterConfigDocument>(
      `${this.baseUrl}/config/admin/${configId}/events`,
      { originalCode, ...eventUpdate }
    );
  }

  deleteAdminCollectionEvent(
    configId: string,
    code: string
  ): Observable<DcRosterConfigDocument> {
    return this.http.delete<DcRosterConfigDocument>(
      `${this.baseUrl}/config/admin/${configId}/events`,
      { params: new HttpParams().set('code', code) }
    );
  }

  getSchedule(filters: DcScheduleFilters): Observable<DcScheduleResponse> {
    let params = new HttpParams()
      .set('page', filters.page)
      .set('pageSize', filters.pageSize);

    if (filters.gameId?.trim()) {
      params = params.set('gameId', filters.gameId.trim());
    }
    if (filters.dcType?.trim()) {
      params = params.set('dcType', filters.dcType.trim());
    }
    if (filters.league?.trim()) {
      params = params.set('league', filters.league.trim());
    }
    if (filters.gameDate?.trim()) {
      params = params.set('gameDate', filters.gameDate.trim());
    }
    if (filters.dcDateFrom?.trim()) {
      params = params.set('dcDateFrom', filters.dcDateFrom.trim());
    }
    if (filters.dcDateTo?.trim()) {
      params = params.set('dcDateTo', filters.dcDateTo.trim());
    }
    if (filters.teamLeaderAuthId?.trim()) {
      params = params.set('teamLeaderAuthId', filters.teamLeaderAuthId.trim());
    }
    if (filters.teamLeaderUnassigned) {
      params = params.set('teamLeaderUnassigned', 'true');
    }
    if (filters.gameStatus?.trim()) {
      params = params.set('gameStatus', filters.gameStatus.trim());
    }
    if (filters.team?.trim()) {
      params = params.set('team', filters.team.trim());
    }

    return this.http.get<DcScheduleResponse>(`${this.baseUrl}/schedule`, {
      params
    });
  }

  getScheduleTeamLeaders(): Observable<TeamLeadersResponse> {
    return this.http.get<TeamLeadersResponse>(
      `${this.baseUrl}/schedule/team-leaders`
    );
  }

  getScheduleDetails(gameId: string): Observable<DcScheduleDetails> {
    return this.http.get<DcScheduleDetails>(
      `${this.baseUrl}/schedule/${gameId}/details`
    );
  }

  getScheduleEventHistory(eventId: string): Observable<DcEventHistoryResponse> {
    return this.http.get<DcEventHistoryResponse>(
      `${this.baseUrl}/schedule/events/${eventId}/history`
    );
  }

  updateScheduleEventCollector(
    eventId: string,
    collectorAuthId: string | null
  ): Observable<DcScheduleDetails> {
    return this.http.patch<DcScheduleDetails>(
      `${this.baseUrl}/schedule/events/${eventId}/collector`,
      { collectorAuthId }
    );
  }

  clearScheduleRoster(gameId: string): Observable<DcScheduleDetails> {
    return this.http.post<DcScheduleDetails>(
      `${this.baseUrl}/schedule/${gameId}/clear-roster`,
      {}
    );
  }

  deleteScheduleRoster(
    gameId: string
  ): Observable<{ deleted: boolean; gameId: string }> {
    return this.http.delete<{ deleted: boolean; gameId: string }>(
      `${this.baseUrl}/schedule/${gameId}/roster`
    );
  }

  updateScheduleTeamLeader(
    gameId: string,
    collectorAuthId: string | null
  ): Observable<{ collectorAuthId: string | null }> {
    return this.http.patch<{ collectorAuthId: string | null }>(
      `${this.baseUrl}/schedule/${gameId}/team-leader`,
      { collectorAuthId }
    );
  }

  markScheduleAssigned(
    gameId: string
  ): Observable<ScheduleMarkAssignedResponse> {
    return this.http.patch<ScheduleMarkAssignedResponse>(
      `${this.baseUrl}/schedule/${gameId}/mark-assigned`,
      {}
    );
  }

  runScheduleAutoAssign(gameIds: string[]): Observable<AutoAssignResponse> {
    return this.http.post<AutoAssignResponse>(
      `${this.baseUrl}/schedule/auto-assign`,
      { gameIds }
    );
  }

  runScheduleAutoAssignForFilters(
    filters: DcScheduleAutoAssignFilters
  ): Observable<AutoAssignResponse> {
    return this.http.post<AutoAssignResponse>(
      `${this.baseUrl}/schedule/auto-assign`,
      { filters }
    );
  }

  downloadScheduleAutoAssignReport(gameIds: string[]): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/schedule/auto-assign/report`,
      { gameIds },
      { responseType: 'blob' }
    );
  }

  getTeamLeaderCoverageConfig(): Observable<TeamLeaderCoverageConfig | null> {
    return this.http
      .get<{ config: TeamLeaderCoverageConfig | null }>(
        `${this.baseUrl}/schedule/team-leader-config`
      )
      .pipe(map((response) => response.config));
  }

  saveTeamLeaderCoverageConfig(
    rows: TeamLeaderCoverageRow[]
  ): Observable<TeamLeaderCoverageConfig> {
    return this.http.put<TeamLeaderCoverageConfig>(
      `${this.baseUrl}/schedule/team-leader-config`,
      { rows }
    );
  }

  previewTeamLeaderAssign(
    filters: DcScheduleAutoAssignFilters
  ): Observable<TeamLeaderAssignPreview> {
    return this.http.post<TeamLeaderAssignPreview>(
      `${this.baseUrl}/schedule/team-leader-assign/preview`,
      { filters }
    );
  }

  runTeamLeaderAssign(
    filters: DcScheduleAutoAssignFilters
  ): Observable<TeamLeaderAssignResult> {
    return this.http.post<TeamLeaderAssignResult>(
      `${this.baseUrl}/schedule/team-leader-assign`,
      { filters }
    );
  }

  save(
    gameId: string,
    dcRoster: DcRoster,
    options: { recalculate?: boolean } = {}
  ): Observable<DcRosterSaveResponse> {
    const params = options.recalculate
      ? new HttpParams().set('recalculate', 'true')
      : undefined;

    return this.http.put<DcRosterSaveResponse>(
      `${this.baseUrl}/${gameId}`,
      {
        ...dcRoster,
        dcTime: this.normalizeOptionalTime(dcRoster.dcTime),
        etcTime: this.normalizeOptionalTime(dcRoster.etcTime)
      },
      { params }
    );
  }

  getTasks(
    page: number,
    pageSize: number,
    includeDone: boolean,
    dcDate?: string | null
  ): Observable<CollectorTaskPage> {
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize)
      .set('includeDone', includeDone);

    params = this.appendDcDateTimeRangeParams(params, dcDate);
    return this.http.get<CollectorTaskPage>(`${this.baseUrl}/tasks`, {
      params
    });
  }

  getOpenTasks(
    page: number,
    pageSize: number,
    dcDate?: string | null
  ): Observable<CollectorTaskPage> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    params = this.appendDcDateTimeRangeParams(params, dcDate);
    return this.http.get<CollectorTaskPage>(`${this.baseUrl}/tasks/open`, {
      params
    });
  }

  updateTaskStatus(
    eventId: string,
    status: TaskStatus | null
  ): Observable<{ status: TaskStatus | null }> {
    return this.http.patch<{ status: TaskStatus | null }>(
      `${this.baseUrl}/tasks/${eventId}/status`,
      { status }
    );
  }

  exchangeTask(
    eventId: string,
    collectorAuthId: string
  ): Observable<{ collectorAuthId: string; status: TaskStatus | null }> {
    return this.http.patch<{
      collectorAuthId: string;
      status: TaskStatus | null;
    }>(`${this.baseUrl}/tasks/${eventId}/exchange`, { collectorAuthId });
  }

  private normalizeOptionalTime(
    value: string | null | undefined
  ): string | null {
    const normalizedValue = value?.trim();
    return normalizedValue ? normalizedValue : null;
  }

  private buildSourceDateTimeRangeForLocalDate(
    normalizedDcDate: string
  ): { from: string; to: string } | null {
    const localDayStart = DateTime.fromISO(normalizedDcDate, {
      zone: 'local'
    }).startOf('day');
    if (
      !localDayStart.isValid ||
      localDayStart.toFormat('yyyy-MM-dd') !== normalizedDcDate
    ) {
      return null;
    }

    return {
      from: localDayStart
        .setZone(TASKS_SOURCE_TIME_ZONE)
        // eslint-disable-next-line @typescript-eslint/quotes
        .toFormat("yyyy-MM-dd'T'HH:mm"),
      to: localDayStart
        .endOf('day')
        .setZone(TASKS_SOURCE_TIME_ZONE)
        // eslint-disable-next-line @typescript-eslint/quotes
        .toFormat("yyyy-MM-dd'T'HH:mm")
    };
  }

  private appendDcDateTimeRangeParams(
    params: HttpParams,
    dcDate?: string | null
  ): HttpParams {
    const normalizedDcDate = dcDate?.trim();
    if (!normalizedDcDate) {
      return params;
    }

    const sourceDateTimeRange =
      this.buildSourceDateTimeRangeForLocalDate(normalizedDcDate);
    if (!sourceDateTimeRange) {
      return params;
    }

    return params
      .set('dcDateTimeFrom', sourceDateTimeRange.from)
      .set('dcDateTimeTo', sourceDateTimeRange.to);
  }

  takeOpenTask(eventId: string): Observable<{ status: TaskStatus | null }> {
    return this.http.patch<{ status: TaskStatus | null }>(
      `${this.baseUrl}/tasks/${eventId}/take`,
      {}
    );
  }
}
